import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactGA from "react-ga4";
import { client } from "@passwordless-id/webauthn";
import {
  AuthFeedback,
  AuthInitialStateFeedback,
  AuthMethod,
  AuthStep,
  Doctor,
  DoctorBaseAttributes,
  FetchPayload,
  ManageAuthFeedback,
  StartAuthChallengeProps,
  AuthFlow,
} from "@interfaces";
import {
  AuthTokens,
  getDetectedDeviceData,
  getStoredTokens,
  setStoredTokens,
  clearStoredTokens,
  isTokenExpired,
  parseFormattedRut,
  resolveAuthChallengeFeedback,
} from "@utils";
import { useFetch } from "./fetch";
import { apiGateway, queryClient } from "./queryClient";
import {
  ENV,
  MEKIDOC_GOOGLE_ADS_ID,
  DOCTOR_REGISTRATION_CONVERSION_ID,
} from "../environment";

interface DoctorPayload {
  doctor: Doctor;
}

interface AuthState {
  tokens: AuthTokens | null;
  isUserAuthed: boolean;
  isRestoringSession: boolean;
  setIsUserAuthed: React.Dispatch<React.SetStateAction<boolean>>;
  startAuthChallenge: ({
    email,
    authMethod,
    authFlow,
  }: StartAuthChallengeProps) => Promise<boolean>;
  authChallengeFeedback: AuthFeedback;
  handleAuthChallengeFeedback: ({
    authMethod,
    authStep,
    changeAfterDelay,
    resetToInitialState,
  }: ManageAuthFeedback) => Promise<void>;
  signIn: (code: string) => Promise<boolean>;
  magicLinkSignIn: (email: string, authCode: string) => Promise<boolean>;
  biometricSignUp: (doctor: Doctor) => Promise<boolean>;
  biometricSignIn: (doctor: Doctor) => Promise<boolean>;
  logout: () => Promise<void>;
  createDoctor: (
    doctorPayload: DoctorPayload,
    authMethod: AuthMethod,
  ) => Promise<Doctor>;
  checkDoctorEmailExists: (email: string) => Promise<boolean>;
  getDoctorFromCommonStack: (rut: string) => Promise<Doctor | null>;
  getDoctorFromRegistrationStack: (
    rut: string,
  ) => Promise<DoctorBaseAttributes | null>;
  resendCode: (
    email: string,
    authMethod: AuthMethod,
    authFlow: AuthFlow,
  ) => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  tokens: null,
  isUserAuthed: false,
  isRestoringSession: true,
  setIsUserAuthed: () => {},
  startAuthChallenge: async () => false,
  authChallengeFeedback: AuthInitialStateFeedback,
  handleAuthChallengeFeedback: async () => {},
  signIn: async () => false,
  magicLinkSignIn: async () => false,
  biometricSignUp: async () => false,
  biometricSignIn: async () => false,
  logout: async () => {},
  createDoctor: async () => {
    throw new Error("createDoctor not implemented");
  },
  checkDoctorEmailExists: async () => false,
  getDoctorFromCommonStack: async () => null,
  getDoctorFromRegistrationStack: async () => null,
  resendCode: async () => {},
});

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside provider");
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const fetch = useFetch();
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isUserAuthed, setIsUserAuthed] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [authChallengeFeedback, setAuthChallengeFeedback] =
    useState<AuthFeedback>(AuthInitialStateFeedback);

  const sessionRef = useRef<string | null>(null);
  const emailRef = useRef<string | null>(null);

  const saveTokens = useCallback((newTokens: AuthTokens) => {
    setTokens(newTokens);
    setStoredTokens(newTokens);
  }, []);

  useEffect(() => {
    async function restoreSession() {
      try {
        const stored = getStoredTokens();
        if (!stored) return;

        if (isTokenExpired(stored.idToken)) {
          try {
            const { idToken, accessToken } = await apiGateway<{
              idToken: string;
              accessToken: string;
            }>({
              method: "POST",
              url: "/auth/refresh-session",
              data: { refreshToken: stored.refreshToken },
            });
            const refreshed: AuthTokens = {
              idToken,
              accessToken,
              refreshToken: stored.refreshToken,
            };
            saveTokens(refreshed);
            setIsUserAuthed(true);
          } catch {
            clearStoredTokens();
          }
          return;
        }

        setTokens(stored);
        setIsUserAuthed(true);
      } finally {
        setIsRestoringSession(false);
      }
    }
    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkDoctorEmailExists = useCallback(
    async (email: string): Promise<boolean> => {
      try {
        const { exists } = await apiGateway<{ exists: boolean }>({
          url: "/auth/exists",
          params: { email },
        });
        return Boolean(exists);
      } catch (error) {
        console.error({
          msg: "Error checking doctor email existence",
          error,
        });
        return false;
      }
    },
    [],
  );

  const getDoctorFromCommonStack = useCallback(
    async (rut: string): Promise<Doctor | null> => {
      try {
        const response = await fetch<{ payload: { doctor: Doctor } }>(
          `/doctors?rut=${rut}`,
        );
        return response.payload.doctor;
      } catch (error) {
        console.error({
          msg: "Error getting doctor from common stack",
          error,
        });
        return null;
      }
    },
    [fetch],
  );

  const handleAuthChallengeFeedback = useCallback(
    async ({
      authMethod,
      authStep,
      changeAfterDelay,
      resetToInitialState,
    }: ManageAuthFeedback) => {
      const delay = 2 * 1000;
      const resolveFeedback = async () => {
        const feedback = resolveAuthChallengeFeedback({
          authMethod,
          authStep,
          resetToInitialState,
        });
        setAuthChallengeFeedback(feedback);
        if (authStep === AuthStep.SUCCESS || authStep === AuthStep.ERROR) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          setAuthChallengeFeedback(AuthInitialStateFeedback);
        }
      };
      if (changeAfterDelay) {
        setTimeout(resolveFeedback, delay);
      } else {
        await resolveFeedback();
      }
    },
    [setAuthChallengeFeedback],
  );

  const startAuthChallenge = useCallback(
    async ({
      email,
      authMethod,
      authFlow,
    }: StartAuthChallengeProps): Promise<boolean> => {
      let authChallengeStarted = false;
      try {
        handleAuthChallengeFeedback({
          authMethod,
          authStep: AuthStep.PENDING,
        });
        const { session } = await apiGateway<{ session: string }>({
          method: "POST",
          url: "/auth/start-challenge",
          data: {
            email,
            authMethod,
            authFlow,
            deviceData: getDetectedDeviceData(),
          },
        });
        sessionRef.current = session;
        emailRef.current = email;
        authChallengeStarted = true;
      } catch (error) {
        console.error({
          msg: "Error starting auth challenge",
          error,
        });
      } finally {
        handleAuthChallengeFeedback({
          authMethod,
          authStep: authChallengeStarted ? AuthStep.PENDING : AuthStep.ERROR,
          resetToInitialState: authChallengeStarted,
          changeAfterDelay: true,
        });
        return authChallengeStarted;
      }
    },
    [handleAuthChallengeFeedback],
  );

  const signIn = useCallback(
    async (code: string): Promise<boolean> => {
      if (!sessionRef.current || !emailRef.current || !code) {
        return false;
      }
      try {
        const result = await apiGateway<{
          idToken: string;
          accessToken: string;
          refreshToken: string;
        }>({
          method: "POST",
          url: "/auth/verify-challenge",
          data: {
            email: emailRef.current,
            code,
            session: sessionRef.current,
          },
        });
        saveTokens({
          idToken: result.idToken,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });
        setIsUserAuthed(true);
        sessionRef.current = null;
        return true;
      } catch (error: any) {
        if (error?.response?.status === 401 && error?.response?.data?.session) {
          sessionRef.current = error.response.data.session;
        }
        return false;
      }
    },
    [saveTokens],
  );

  const magicLinkSignIn = useCallback(
    async (email: string, authCode: string) => {
      handleAuthChallengeFeedback({
        authMethod: AuthMethod.MAGIC_LINK,
        authStep: AuthStep.VALIDATING,
      });
      try {
        const result = await apiGateway<{
          idToken: string;
          accessToken: string;
          refreshToken: string;
        }>({
          method: "POST",
          url: "/auth/verify-challenge",
          data: {
            email,
            code: authCode,
            authMethod: AuthMethod.MAGIC_LINK,
          },
        });
        saveTokens({
          idToken: result.idToken,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });
        setIsUserAuthed(true);
        await handleAuthChallengeFeedback({
          authMethod: AuthMethod.MAGIC_LINK,
          authStep: AuthStep.SUCCESS,
        });
        return true;
      } catch {
        await handleAuthChallengeFeedback({
          authMethod: AuthMethod.MAGIC_LINK,
          authStep: AuthStep.ERROR,
        });
        return false;
      }
    },
    [handleAuthChallengeFeedback, saveTokens],
  );

  const biometricSignUp = useCallback(
    async (doctor: Doctor) => {
      const { rut, email, phone } = doctor;
      const formattedRut = parseFormattedRut(rut);
      try {
        handleAuthChallengeFeedback({
          authMethod: AuthMethod.BIOMETRIC_REGISTRATION,
          authStep: AuthStep.PENDING,
        });
        const { challenge } = await fetch<{ challenge: string }>(
          `/biometric-challenge/${formattedRut}`,
        );
        const registation = await client.register(formattedRut, challenge, {
          authenticatorType: "both",
          userVerification: "required",
        });
        handleAuthChallengeFeedback({
          authMethod: AuthMethod.BIOMETRIC_REGISTRATION,
          authStep: AuthStep.VALIDATING,
        });
        await fetch(`/biometric-registration`, {
          method: "POST",
          body: JSON.stringify({
            registration: {
              ...registation,
              userRut: formattedRut,
              userEmail: email,
              userPhone: phone,
            },
            origin: window.location.origin,
          }),
        });
        await handleAuthChallengeFeedback({
          authMethod: AuthMethod.BIOMETRIC_REGISTRATION,
          authStep: AuthStep.SUCCESS,
        });
        return true;
      } catch (error) {
        const userCancellationMessage =
          "The operation either timed out or was not allowed.";
        if (error instanceof DOMException) {
          if (error.message.includes(userCancellationMessage)) {
            console.error({
              msg: "User canceled biometric registration",
              error,
            });
          }
        } else {
          console.error({
            msg: "Biometric registration error",
            error,
          });
        }
        await handleAuthChallengeFeedback({
          authMethod: AuthMethod.BIOMETRIC_REGISTRATION,
          authStep: AuthStep.ERROR,
        });
        return false;
      } finally {
        queryClient.invalidateQueries({
          queryKey: ["doctor"],
        });
      }
    },
    [fetch, handleAuthChallengeFeedback],
  );

  const biometricSignIn = useCallback(
    async (doctor: Doctor) => {
      const { rut, email } = doctor;
      const formattedRut = parseFormattedRut(rut);
      try {
        handleAuthChallengeFeedback({
          authMethod: AuthMethod.BIOMETRIC_AUTH,
          authStep: AuthStep.PENDING,
        });
        const { credentialsIds } = await fetch<{ credentialsIds: string[] }>(
          `/biometric-credentials/${formattedRut}`,
        );
        if (!credentialsIds.length) {
          return false;
        }
        const { challenge } = await fetch<{ challenge: string }>(
          `/biometric-challenge/${formattedRut}`,
        );
        const encodedAuthentication = await client.authenticate(
          credentialsIds,
          challenge,
          {
            authenticatorType: "both",
            userVerification: "required",
          },
        );
        handleAuthChallengeFeedback({
          authMethod: AuthMethod.BIOMETRIC_AUTH,
          authStep: AuthStep.VALIDATING,
        });
        const result = await apiGateway<{
          idToken: string;
          accessToken: string;
          refreshToken: string;
        }>({
          method: "POST",
          url: "/auth/verify-biometric",
          data: {
            email,
            authentication: encodedAuthentication,
            origin: window.location.origin,
          },
        });
        saveTokens({
          idToken: result.idToken,
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });
        setIsUserAuthed(true);
        await handleAuthChallengeFeedback({
          authMethod: AuthMethod.BIOMETRIC_AUTH,
          authStep: AuthStep.SUCCESS,
        });
        return true;
      } catch (error) {
        const userCancellationMessage =
          "The operation either timed out or was not allowed.";
        if (error instanceof DOMException) {
          if (error.message.includes(userCancellationMessage)) {
            console.error({
              msg: "User canceled biometric authentication",
              error,
            });
          }
        } else {
          console.error({
            msg: "Biometric authentication error",
            error,
          });
        }
        await handleAuthChallengeFeedback({
          authMethod: AuthMethod.BIOMETRIC_AUTH,
          authStep: AuthStep.ERROR,
        });
        return false;
      } finally {
        queryClient.invalidateQueries({
          queryKey: ["doctor"],
        });
      }
    },
    [fetch, handleAuthChallengeFeedback, saveTokens],
  );

  const logout = useCallback(async () => {
    try {
      await apiGateway({ method: "POST", url: "/auth/logout" });
    } catch (error) {
      console.error({ msg: "Error calling logout endpoint", error });
    } finally {
      clearStoredTokens();
      setTokens(null);
      setIsUserAuthed(false);
      await queryClient.resetQueries(
        { queryKey: ["doctor"] },
        { cancelRefetch: true },
      );
    }
  }, []);

  const createDoctor = useCallback(
    async (
      doctorPayload: DoctorPayload,
      authMethod: AuthMethod,
    ): Promise<Doctor> => {
      const response = await fetch<FetchPayload<DoctorPayload>>(`/doctors`, {
        method: "POST",
        body: JSON.stringify(doctorPayload),
      });
      if (!response.payload.doctor) {
        throw Error("No doctor created");
      }
      const { doctor } = response.payload;
      const { email } = doctor;
      await startAuthChallenge({
        email,
        authMethod,
        authFlow: AuthFlow.LOGIN,
      });
      if (ENV === "production") {
        ReactGA.gtag("event", "conversion", {
          send_to: `${MEKIDOC_GOOGLE_ADS_ID}/${DOCTOR_REGISTRATION_CONVERSION_ID}`,
        });
      }
      return doctor;
    },
    [fetch, startAuthChallenge],
  );

  const getDoctorFromRegistrationStack = useCallback(async (rut: string) => {
    try {
      const payload = await apiGateway<DoctorBaseAttributes>({
        url: "/doctor-registration/validate-doctor",
        params: { rut },
      });
      return Object.keys(payload).length > 0 ? payload : null;
    } catch (error) {
      console.error({
        msg: "Error getting doctor from registration stack",
        error,
      });
      return null;
    }
  }, []);

  const resendCode = useCallback(
    async (email: string, authMethod: AuthMethod, authFlow: AuthFlow) => {
      try {
        handleAuthChallengeFeedback({
          authMethod,
          authStep: AuthStep.PENDING,
        });
        const { session } = await apiGateway<{ session: string }>({
          method: "POST",
          url: "/auth/resend-code",
          data: {
            email,
            session: sessionRef.current,
            authMethod,
            deviceData: getDetectedDeviceData(),
          },
        });
        sessionRef.current = session;
        handleAuthChallengeFeedback({
          resetToInitialState: true,
          changeAfterDelay: true,
        });
      } catch (error) {
        console.error({ msg: "Error resending code", error });
        const parsedError = String(error);
        if (parsedError.includes("NotAuthorizedException")) {
          await startAuthChallenge({ email, authMethod, authFlow });
        } else {
          handleAuthChallengeFeedback({
            authMethod,
            authStep: AuthStep.ERROR,
            changeAfterDelay: true,
          });
        }
      }
    },
    [handleAuthChallengeFeedback, startAuthChallenge],
  );

  const value = useMemo(
    () => ({
      tokens,
      isUserAuthed,
      isRestoringSession,
      setIsUserAuthed,
      startAuthChallenge,
      authChallengeFeedback,
      handleAuthChallengeFeedback,
      signIn,
      magicLinkSignIn,
      biometricSignUp,
      biometricSignIn,
      logout,
      createDoctor,
      checkDoctorEmailExists,
      getDoctorFromCommonStack,
      getDoctorFromRegistrationStack,
      resendCode,
    }),
    [
      tokens,
      isUserAuthed,
      isRestoringSession,
      setIsUserAuthed,
      startAuthChallenge,
      authChallengeFeedback,
      handleAuthChallengeFeedback,
      signIn,
      magicLinkSignIn,
      biometricSignUp,
      biometricSignIn,
      logout,
      createDoctor,
      checkDoctorEmailExists,
      getDoctorFromCommonStack,
      getDoctorFromRegistrationStack,
      resendCode,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
